<script setup lang="ts">
import type { JobItem, JobItemPlatform } from '~/models/Job'
interface Props {
  userToken: string | null
  job: JobItem
  withTitle: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['isIntersect'])
const { t } = useI18n()
const route = useRoute()
const modalOpen = ref(false)
const applyButton = ref(null)
const jobApplyUrls = ref<JobItemPlatform[]>()
const isLoading = ref(true)
const isError = ref(false)
const isHidden = ref(false)
useIntersectionObserver(
  applyButton,
  ([{ isIntersecting }], observerElement) => {
    emit('isIntersect', isIntersecting)
    isHidden.value = !isIntersecting
  },
  {
    rootMargin: '0%',
  },
)

onMounted(async () => {
  await nextTick()
  checkApplyAvailability()
})

async function checkApplyAvailability() {
  isLoading.value = true

  const { data, error } = await useJobApi(`/jobs/${props.job?.job_id}/platforms`, {
    method: 'GET',
    useAuth: true,
    key: 'check_platform',
  })
  if (data.value)
    jobApplyUrls.value = data.value.filter((item: JobItemPlatform) => item.validity_check.isValid || (!item.validity_check.isValid && item.validity_check.code === '429'))
  else isError.value = true
  isLoading.value = false
}
</script>

<template>
  <div class="justify-center">
    <ContinueFormModal :open="modalOpen" :redirect-val="route.fullPath" @open-modal="(item: boolean) => modalOpen = item" />
    <!-- if loading -->
    <div v-if="isLoading" class="py-2.5 px-4 bg-white text-[#5843E5] font-bold flex items-center">
      <div class="flex-shrink-0 flex items-center mr-2">
        <Icon name="eos-icons:loading" class="w-5 h-5" />
      </div>
      Checking job availability...
    </div>
    <!-- if there are error -->
    <div v-if="!isLoading && isError" class="py-2.5 px-4 bg-[#FFF7ED] text-[#DC2626] font-bold flex items-center">
      <div class="flex-shrink-0 flex items-center mr-2">
        <Icon name="material-symbols:warning-outline" class="w-5 h-5" />
      </div>
      Failed to check availability
      <div class="text-[#5843E5] cursor-pointer ml-2" @click="checkApplyAvailability()">
        Retry
      </div>
    </div>
    <!-- if job apply url exist -->
    <div v-if="!isLoading && !isError">
      <p v-if="props.withTitle" class="mb-4 text-gray-600 text-xs">
        {{ $t('job_detail_apply_on') }}
      </p>
      <div :class="`${props.job.platforms.length <= 3 ? `sm:grid-cols-${props.job.platforms.length}` : 'sm:grid-cols-3'} grid grid-cols-1 items-center gap-x-4 gap-y-2`">
        <JobsApplyButtonAll v-for="(platform, idx) in props.job.platforms" :key="idx" :item="platform" :job="props.job" @apply-clicked="modalOpen = true" />
      </div>
      <!-- if no job apply url available -->
      <div v-if="isEmpty(jobApplyUrls)" class="py-2 px-4 mt-2 bg-gray-50 rounded-md text-gray-600">
        <p class="text-sm font-semibold mb-2 flex-shrink-0 flex items-center">
          <Icon name="material-symbols:info-outline" class="w-4 h-4 mr-2 text-[#5843E5]" />
          Availability Status
        </p>
        <p class="text-xs">
          This job is expected to be in high demand and may close soon. We’ll remove this job ad once it's closed.
        </p>
      </div>
    </div>
  </div>
</template>
